import { useDebouncedValue, useLocalStorage } from "@mantine/hooks"
import {
  Check,
  Checks,
  Circle,
  Clock,
  CreditCard,
  CurrencyInr,
  DotsThreeVertical,
  DownloadSimple,
  FileRs,
  Handshake,
  Info,
  List,
  MagnifyingGlass,
  Receipt,
  Wallet,
  X,
} from "@phosphor-icons/react"
import { openConfirmDialog } from "@vesatogo/grass-confirm-modal"
import {
  Avatar,
  CodenameTag,
  InputGroup,
  Menu,
  MenuItem,
  Popover,
  SegmentedControl,
  Toast,
  Tooltip,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { pick } from "lodash-es"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useClient } from "urql"
import { useAuthorization } from "~/components/AuthorizationProvider"
import DownloadExcelButton from "~/components/DownloadExcelButton"
import { RemarkModal } from "~/components/Remark/RemarkModal"
import { TableSummary } from "~/components/TableSummary"
import { InvoiceTransactionModal } from "~/components/Transaction/InvoiceTransactionDetail"
import ReturnTransactionsModal from "~/components/Transaction/ReturnTransactions"
import { HistoryColumns } from "~/components/Transaction/TransactionHistory"
import Codenames from "~/constants/Codenames"
import FilterTypes from "~/constants/FilterTypes"
import { PayableReceivableDate } from "~/constants/Payable"
import { Permission } from "~/constants/Permissions"
import { siteConfig } from "~/constants/config"
import { AppRoutes } from "~/constants/routes"
import { TwilioMessages2 } from "~/constants/static.items"
import {
  BuyerOverAllOutstandingDocument,
  InvoiceSettlementDocument,
  Invoice_Bool_Exp,
  Order_By,
  useAuthenticatedUserQuery,
  useCancelInvoicesMutation,
  useInvoiceSettlementDetailOverduesQuery,
  useInvoiceSettlementQuery,
  useInvoiceSummaryAnalyticsQuery,
  useUpdateInvoiceSettlementPaymentMutation,
} from "~/generated/graphql"
import SettlementActions from "~/pages/d/bank-reconciliation/SettlementActions"
import PayableFilter, {
  PayableFilterType,
} from "~/pages/d/payable/advance/PayableFilter"
import { usePayableSelector } from "~/pages/d/payable/advance/PayableSelector"
import { calculateFinalAmount } from "~/pages/pavti/lib/calculate-bifurcation-total"
import { inrFormatter } from "~/utils/inrFormatter"

const OverdueInfo = ({ invoiceId }: { invoiceId: any }) => {
  const [opened, setOpened] = useState(false)
  const [{ data, fetching }] = useInvoiceSettlementDetailOverduesQuery({
    variables: { id: invoiceId },
    pause: !opened || !invoiceId,
  })

  return (
    <Tooltip
      content={
        <div className="text-sm p-1 whitespace-nowrap">
          {fetching && !data ? (
            "Loading..."
          ) : (
            <>
              <div>
                Overdue Amount:{" "}
                <span className="font-700">
                  {inr(data?.invoice_by_pk?.summary?.[0]?.overdue_amount || 0)}
                </span>
              </div>
              <div>
                Overdue Days:{" "}
                <span className="font-700">
                  {data?.invoice_by_pk?.summary?.[0]?.overdue_days || 0}
                </span>
              </div>
            </>
          )}
        </div>
      }
    >
      <div
        className="flex items-center gap-1 cursor-pointer"
        onMouseEnter={() => setOpened(true)}
      >
        <Info size={20} weight="fill" className="text-blue-400" />
      </div>
    </Tooltip>
  )
}

const historyColumns = [
  HistoryColumns.DATE?.accessor,
  HistoryColumns.PAYMENT_OPT?.accessor,
  HistoryColumns.PAYMENT_MODE?.accessor,
  HistoryColumns.TRANSACTION_ID?.accessor,
  HistoryColumns.PARENT_TRANSACTION_ID?.accessor,
  HistoryColumns.RELEASED_AMOUNT?.accessor,
  HistoryColumns.NARRATION?.accessor,
]
export const InvoiceSettlement = () => {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const page = Number(params.get("page")) || 1
  const search = params.get("search")
  const [department] = useLocalStorage({ key: "department" })
  const { selected } = usePayableSelector()
  const [invoiceId, setInvoiceId] = useState()
  const isTrade = selected === "trade"
  const [debouncedSearch] = useDebouncedValue(
    search?.trim(),
    siteConfig.debounce
  )
  const isTransactionOpen = params.get("transaction") === "true"
  const [{ data: authData }] = useAuthenticatedUserQuery()
  const [, cancelInvoice] = useCancelInvoicesMutation()
  const me = authData?.me
  const client = useClient()

  const [filter, setFilter] = useState<PayableFilterType>(() => {
    const savedFilter = localStorage.getItem(FilterTypes.invoiceFilter)
    return savedFilter ? JSON.parse(savedFilter) : {}
  })

  useEffect(() => {
    localStorage.setItem(FilterTypes.invoiceFilter, JSON.stringify(filter))
  }, [filter])

  const where: Invoice_Bool_Exp = {
    buyer_settlement_status: filter?.transaction_status?.length
      ? {
          _in: filter?.transaction_status?.map(st => st?.id),
        }
      : undefined,
    department: filter?.department
      ? {
          reference_id: {
            _eq: filter?.department?.codename,
          },
        }
      : {
          reference_id: {
            _in: me?.agent?.departments?.map(
              dept => dept?.department?.codename
            ) as any,
          },
        },
    ...(filter?.placed_by
      ? {
          created_by_id: {
            _eq: filter?.placed_by?.id?.toString(),
          },
        }
      : undefined),
    created_at: {
      _lte: filter?.date_to ? dayjs(filter?.date_to).endOf("d") : undefined,
      _gte: filter?.date_from
        ? dayjs(filter?.date_from).startOf("d")
        : undefined,
      _gt: dayjs(PayableReceivableDate).startOf("d"),
    },

    buyer: filter?.seller?.codename
      ? {
          reference_id: {
            _eq: filter?.seller?.codename,
          },
        }
      : undefined,

    ...(debouncedSearch
      ? {
          id:
            debouncedSearch && Number(debouncedSearch)
              ? {
                  _eq: Number(debouncedSearch),
                }
              : undefined,
        }
      : undefined),
    ...(debouncedSearch && !Number(debouncedSearch)
      ? {
          _or: debouncedSearch
            ? [
                {
                  department: debouncedSearch
                    ? {
                        name: {
                          _ilike: `%${debouncedSearch}%`,
                        },
                      }
                    : undefined,
                },
                {
                  buyer: debouncedSearch
                    ? {
                        name: {
                          _ilike: `%${debouncedSearch}%`,
                        },
                      }
                    : undefined,
                },
              ]?.filter(abc => abc)
            : undefined,
        }
      : undefined),
  }

  const [{ data, fetching: listFetching }, refetchList] =
    useInvoiceSettlementQuery({
      variables: {
        where,
        limit: siteConfig.pageSize,
        offset: (page - 1) * siteConfig.pageSize,
        orderBy: {
          created_at: Order_By.DescNullsLast,
        },
      },
      pause: isTransactionOpen || !!invoiceId,
    })

  const totalRows = data?.invoice_aggregate?.aggregate?.count || 0

  const canUploadPayableReceivable = useAuthorization(
    Permission.canUploadPayableReceivable
  )
  const canReturnTransaction = useAuthorization(
    Permission.CanReturnTransactions
  )
  const canCancelInvoice = useAuthorization(Permission.CanCancelInvoice)

  const totalCredit = data?.invoice?.reduce(
    (acc, inv) =>
      acc + (Number(inv?.summary?.[0]?.buyer_credit_note_amount) || 0),
    0
  )
  const totalDebit = data?.invoice?.reduce(
    (acc, inv) =>
      acc + (Number(inv?.summary?.[0]?.buyer_debit_note_amount) || 0),
    0
  )

  const getExcelRows = async () => {
    const { data: excelData, error } = await client
      .query(InvoiceSettlementDocument, {
        requestPolicy: "network-only",
        where,
        orderBy: {
          created_at: Order_By.DescNullsLast,
        },
      })
      .toPromise()

    if (error) {
      const status = error?.response?.status
      if (status >= 500) {
        throw new Error(
          "The server is experiencing issues right now. Please try again later."
        )
      }
      throw new Error(
        `Failed to load invoice settlement data. ${error.message}`
      )
    }

    const toAmount = (value: number) => {
      const rounded = Math.round((value || 0) * 100) / 100
      return rounded === 0 ? 0 : rounded
    }

    const rows = (excelData?.invoice || []).map(inv => {
      const bifurcation = inv.buyer_bifurcation
      const deducted = Number(bifurcation?.deducted_amount?.val) || 0
      const tax = Number(bifurcation?.buyer_tax_amount?.val) || 0
      const expense = Number(bifurcation?.buyer_expense_amount?.val) || 0
      const expenseTax = Number(bifurcation?.buyer_expense_tax_amount?.val) || 0
      const invoiceValue = toAmount(deducted + tax + expense + expenseTax)
      const creditNote = toAmount(
        Number(bifurcation?.buyer_credit_note_amount?.val)
      )
      const debitNote = toAmount(
        Number(bifurcation?.buyer_debit_note_amount?.val)
      )
      const received = toAmount(Number(bifurcation?.received_amount?.val))
      const receivable = toAmount(Number(bifurcation?.total))

      const isInvoiceCancelled =
        inv.current_status_id === Codenames.InvoiceCancelled
      const status = isInvoiceCancelled
        ? "Cancelled"
        : inv?.buyer_settlement_status

      return {
        "Created On": dayjs(inv.created_at).format("D MMM 'YY"),
        "Created Time": dayjs(inv.created_at).format("hh:mm a"),
        "Invoice No.": inv.id ? `Invoice #${inv.id}` : "--",
        "Plant Name": inv.department?.name || "--",
        Customer: inv.buyer?.user?.[0]?.full_name || "--",
        "Customer Codename":
          inv.buyer?.user?.[0]?.external_reference_id2 || "--",
        "Invoice Value": invoiceValue,
        "Credit Note": creditNote,
        "Debit Note": debitNote,
        "Received Amount": received,
        "Receivable Amount": receivable,
        "Placed By": inv.created_by?.full_name || "--",
        "Transaction Status": status,
        "Last Transaction": inv.transactions?.[0]?.date
          ? dayjs(inv.transactions[0].date).format("DD MMM 'YYYY")
          : "--",
        Remark: inv.remark || "--",
      }
    })

    return rows
  }

  const [{ data: analyticsData, fetching: analyticsLoading }] =
    useInvoiceSummaryAnalyticsQuery({
      variables: {
        buyerReferenceId: filter?.seller?.codename,
        createdFrom: filter?.date_from
          ? dayjs(filter?.date_from).format("YYYY-MM-DD")
          : dayjs(PayableReceivableDate).format("YYYY-MM-DD"),
        createdTill: filter?.date_to
          ? dayjs(filter?.date_to).format("YYYY-MM-DD")
          : undefined,
        department: filter?.department
          ? [filter?.department?.codename]
          : (me?.agent?.departments?.map(
              dept => dept?.department?.codename
            ) as any),
        createdById: filter.placed_by?.id ? filter.placed_by?.id : undefined,
        search: debouncedSearch,
      },
    })
  const COLUMNS = [
    {
      Header: "Created On",
      accessor: "created_at",
      Cell({ value }) {
        // if (!value) return "--"
        return (
          <Cell
            title={dayjs(value).format("D MMM 'YY")}
            subtitle={dayjs(value).format("hh:mm a")}
          />
        )
      },
      formatter: () => {
        return "Total"
      },
    },
    {
      Header: "Invoice No.",
      accessor: "id",
      Cell({ value, row: { original } }) {
        if (!value) return "--"
        return (
          <div className="pl-4">
            <div
              className="link-text text-left w-fit cursor-pointer"
              onClick={() => {
                params.set("transaction", "true")
                params.set("invoiceId", original?.id)
                setParams(params)
              }}
            >
              Invoice #{value}
            </div>
          </div>
        )
      },
    },
    {
      Header: "Plant Name",
      accessor: "department.name",
      Cell({ value }) {
        return <Cell title={value} />
      },
    },
    {
      Header: "Customer",
      accessor: "buyer.user.0",
      Cell({ value }) {
        return (
          <Cell
            title={value.full_name}
            subtitle={
              <CodenameTag referenceId={value.external_reference_id2} />
            }
          />
        )
      },
    },
    {
      Header: "Invoice Value",
      accessor: "buyer_bifurcation.deducted_amount",
      Cell({ value, row: { original } }) {
        if (!value) return "--"
        const gst = original?.buyer_bifurcation?.buyer_tax_amount?.val
        const expense = original?.buyer_bifurcation?.buyer_expense_amount?.val
        const expenseTax =
          original?.buyer_bifurcation?.buyer_expense_tax_amount?.val

        const total =
          Number(value?.val) +
          Number(gst) +
          Number(expense) +
          Number(expenseTax)
        return (
          <Cell
            titleClassName="font-700 !text-green"
            title={
              <Tooltip
                content={`Base: ${inr(value?.val)} | GST: ${inr(
                  gst
                )} | Expense: ${inr(expense)} | Expense Tax: ${inr(
                  expenseTax
                )}`}
              >
                <div>{inr(total)}</div>
              </Tooltip>
            }
            subtitle={gst ? "Incl. of taxes" : null}
          />
        )
      },
      formatter: () => {
        const totalInvoiceValue =
          data?.invoice
            ?.map(
              (inv: any) =>
                Number(inv?.buyer_bifurcation?.deducted_amount?.val) +
                Number(inv?.buyer_bifurcation?.buyer_tax_amount?.val) +
                Number(inv?.buyer_bifurcation?.buyer_expense_amount?.val) +
                Number(inv?.buyer_bifurcation?.buyer_expense_tax_amount?.val)
            )
            ?.reduce((a: any, b: any) => a + b, 0) || 0
        return inrFormatter.format(totalInvoiceValue)
      },
    },
    {
      Header: "Credit Note",
      accessor: "buyer_bifurcation.buyer_credit_note_amount",
      id: "creditNote",
      Cell({ value }) {
        if (!value) return "--"
        return (
          <Cell
            titleClassName="font-700 !text-red"
            title={`${value?.op + inr(value?.val)}`}
          />
        )
      },
      formatter: () => {
        return inrFormatter.format(totalCredit || 0)
      },
    },
    {
      Header: "Debit Note",
      accessor: "buyer_bifurcation.buyer_debit_note_amount",
      id: "debitNote",
      Cell({ value }) {
        if (!value) return "--"
        return (
          <Cell
            titleClassName="font-700 !text-green"
            title={`${value?.op + inr(value?.val)}`}
          />
        )
      },
      formatter: () => {
        return inrFormatter.format(totalDebit || 0)
      },
    },
    {
      Header: "Received Amount",
      accessor: "buyer_bifurcation.received_amount.val",
      Cell({ value }) {
        if (!value) return "--"
        return <Cell titleClassName="font-700 !text-green" title={inr(value)} />
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "Receivable Amount",
      id: "buyer_bifurcation.total",
      accessor: "buyer_bifurcation.total",
      Cell({ value }) {
        // if (!value) return "--"
        return <Cell titleClassName="font-700 !text-red" title={inr(value)} />
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "Placed By",
      accessor: "created_by",
      Cell({ value, row }) {
        if (!value) return "--"
        return (
          <Tooltip
            content={
              <div>
                <p>{value?.full_name || "na"}</p>
                <p className="text-gray-400">
                  {dayjs(row?.original?.created_at).format(
                    "DD MMM 'YY | h:mm a"
                  )}
                </p>
              </div>
            }
          >
            <Avatar
              className="relative text-center"
              name={value?.full_name}
              src={value?.photo?.url}
            />
          </Tooltip>
        )
      },
    },
    {
      Header: "Transaction Status",
      accessor: "buyer_settlement_status",
      Cell({ row: { original }, value }) {
        const paid = value === "partial"
        const isSettled = value === "settled"
        const isInvoiceCancelled =
          original?.current_status_id === Codenames.InvoiceCancelled

        return (
          <Cell
            titleClassName={classNames(
              "flex gap-1 items-center font-600 ",
              isInvoiceCancelled
                ? "!text-red"
                : isSettled
                ? "!text-green"
                : paid
                ? "!text-blue"
                : "!text-yellow"
            )}
            title={
              <>
                <Circle weight="fill" />
                {isInvoiceCancelled
                  ? "Cancelled"
                  : isSettled
                  ? "Settled"
                  : paid
                  ? "Partial"
                  : "Pending"}
              </>
            }
          />
        )
      },
    },
    {
      Header: "Last Transaction",
      accessor: "transactions.0.date",
      Cell({ value }) {
        if (!value) return "--"
        return <Cell title={<>{dayjs(value).format("DD MMM 'YYYY")}</>} />
      },
    },
    {
      Header: "Remark",
      accessor: "remark",
      Cell({ value }) {
        return <>{value}</>
      },
    },
    {
      Header: "",
      accessor: "updated_at",
      Cell({ row: { original } }) {
        const [params, setParams] = useSearchParams()
        const twilioStatus =
          TwilioMessages2[original?.metadata?.current_message_status]

        const totalAmount = original?.buyer_payable
        const paid = original?.transactions_aggregate?.aggregate?.sum?.amount
        const remaining = Number((totalAmount - paid).toFixed(2))
        const isSettled = remaining === 0 || remaining < 0
        const currentStatus = original?.current_status_id
        const isInvoiceCancelled = currentStatus === Codenames.InvoiceCancelled
        const issuanceNote =
          original?.issuance_notes_aggregate?.aggregate?.count || 0
        return (
          <div className="flex items-center gap-1">
            <OverdueInfo invoiceId={original.id} />
            <Popover
              minimal
              arrow={false}
              className="p-0"
              trigger="click"
              interactive
              placement="bottom"
              content={
                <Menu className="max-h-72">
                  <MenuItem
                    onClick={() => {
                      params.set("remark", original.id)
                      setParams(params)
                    }}
                  >
                    Remark
                  </MenuItem>
                  <MenuItem
                    onClick={async () => {
                      const token = localStorage.getItem("token")

                      const { data: fetchedData } = await client
                        .query(BuyerOverAllOutstandingDocument, {
                          user_id: original?.buyer?.id,
                        })
                        .toPromise()

                      const number = original?.buyer?.user?.[0]?.number
                      if (!number) {
                        toast.error("Buyer has no registered number!", {
                          duration: 3000,
                        })
                        return
                      }

                      const body = {
                        to: number,
                        statusCallback: `https://${
                          import.meta.env.VITE_REST_URL
                        }/rest/trade/status-callback/?outstanding_invoice_id=${
                          original?.id
                        }`,
                        notificationTemplateCodename:
                          "outstanding-payment-reminder-manual",
                        contentVariables: {
                          invoice_no: original?.id?.toString(),
                          invoice_date: dayjs(original?.created_at).format(
                            "DD MMM YYYY"
                          ),
                          plant_name: original?.department?.name,
                          invoice_value: inr(original?.buyer_payable || 0),
                          received_amount: inr(
                            original?.transactions_aggregate?.aggregate?.sum
                              ?.amount || 0
                          ),
                          receivable_amount: inr(
                            (original?.buyer_payable || 0) -
                              (original?.transactions_aggregate?.aggregate?.sum
                                ?.amount || 0)
                          ),
                          overdue_days: String(
                            original?.summary?.[0]?.overdue_days || 0
                          ),
                          overall_outstand: inr(
                            fetchedData?.credit_transaction_aggregate?.aggregate
                              ?.sum?.outstanding_amount || 0
                          ),
                        },
                      }
                      const data = await fetch(
                        `https://${
                          import.meta.env.VITE_REST_URL
                        }/notification/send_whatsapp_msg/`,
                        {
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                          },
                          method: "POST",
                          body: JSON.stringify(body),
                        }
                      )

                      toast.success("Whatsapp message sent successfully!", {
                        duration: 3000,
                      })
                    }}
                  >
                    <div className="flex justify-between gap-2">
                      <p>O/S Payment Reminder</p>
                      <div className="flex gap-1">
                        {twilioStatus?.id === "queued" && (
                          <Clock
                            size={20}
                            weight="bold"
                            color={twilioStatus.color}
                          />
                        )}
                        {twilioStatus?.id === "sent" && (
                          <Check
                            size={20}
                            weight="bold"
                            color={twilioStatus.color}
                          />
                        )}
                        {twilioStatus?.id === "delivered" && (
                          <Checks
                            size={20}
                            weight="bold"
                            color={twilioStatus.color}
                          />
                        )}
                        {twilioStatus?.id === "read" && (
                          // use a distinct blue for read (WhatsApp-style blue)
                          <Checks size={20} weight="bold" color="#1877F2" />
                        )}
                        {twilioStatus?.id === "failed" && (
                          <X
                            size={20}
                            weight="bold"
                            color={twilioStatus.color}
                          />
                        )}
                      </div>
                    </div>
                  </MenuItem>
                  <MenuItem
                    disabled={isInvoiceCancelled}
                    hidden={!canReturnTransaction}
                    onClick={() => {
                      setInvoiceId(original?.id)
                    }}
                  >
                    Return Transactions
                  </MenuItem>
                  <MenuItem
                    disabled={isInvoiceCancelled}
                    hidden={!canCancelInvoice}
                    onClick={() => {
                      const onSubmit = async () => {
                        const { data } = await cancelInvoice({
                          invoiceId: original?.id as any,
                        })
                        if (data?.cancel_invoice?.success) {
                          toast.custom(
                            <Toast title="Invoice cancelled successfully" />
                          )
                          setTimeout(() => {
                            refetchList({
                              requestPolicy: "network-only",
                            })
                          }, 1000)
                        } else {
                          toast.custom(
                            <Toast
                              title="Failed to cancel invoice"
                              intent="danger"
                            />
                          )
                        }
                      }
                      if (issuanceNote > 0) {
                        return openConfirmDialog({
                          isOpen: true,
                          onSubmit: () => {
                            return
                          },
                          message:
                            "Cannot Cancel Invoice. Credit/ Debit note is created!",
                          secondaryButtonProps: {
                            hidden: true,
                          },
                        })
                      }
                      if (isSettled) {
                        openConfirmDialog({
                          isOpen: true,
                          onSubmit: onSubmit,
                          primaryButtonProps: {
                            intent: "primary",
                            variant: "solid",
                            text: "Cancel Invoice",
                          },
                          secondaryButtonProps: {
                            text: "Go Back",
                          },
                          message:
                            "Invoice is already Settled! Are you sure you want to Cancel Invoice?",
                        })
                      } else {
                        onSubmit()
                      }
                    }}
                  >
                    Cancel Invoice
                  </MenuItem>
                </Menu>
              }
            >
              <button className="hover:bg-gray-300 rounded">
                <DotsThreeVertical size={20} />
              </button>
            </Popover>
          </div>
        )
      },
    },
  ]

  const summaryCards = [
    {
      id: "total",
      title: "Total Invoice Amount",
      value: Number(
        Number(
          calculateFinalAmount(
            pick(
              analyticsData?.aggregate_invoice_summary as any,
              "deducted_amount",
              "buyer_tax_amount",
              "buyer_expense_amount",
              "buyer_expense_tax_amount"
            )
          ) as any
        ).toFixed(2)
      ),
      helper: "",
      intentClass: "text-blue-400",
      iconBgClass: "bg-blue-100",
      icon: FileRs,
    },
    {
      id: "credit",
      title: "Credit Note Amount",
      value: Number(
        Number(
          analyticsData?.aggregate_invoice_summary?.buyer_credit_note_amount
            ?.val as any
        )?.toFixed(2)
      ),
      helper: "",
      intentClass: "text-red-500",
      iconBgClass: "bg-red-100",
      icon: CreditCard,
      valueClassName: "!text-red-500",
    },
    {
      id: "debit",
      title: "Debit Note Amount",
      value: Number(
        Number(
          analyticsData?.aggregate_invoice_summary?.buyer_debit_note_amount
            ?.val as any
        )?.toFixed(2)
      ),
      helper: "",
      intentClass: "text-green-300",
      iconBgClass: "bg-green-100",
      valueClassName: "!text-green-300",
      icon: Handshake,
    },

    {
      id: "received",
      title: "Received Amount",
      value: Number(
        Number(
          analyticsData?.aggregate_invoice_summary?.received_amount?.val as any
        )?.toFixed(2)
      ),
      intentClass: "text-yellow-500",
      iconBgClass: "bg-yellow-100",
      icon: Handshake,
    },
    {
      id: "balance",
      title: "Balance Amount",
      value: Number(
        Number(
          analyticsData?.aggregate_invoice_summary
            ?.department_settlement_remaining_amount as any
        )?.toFixed(2)
      ),
      tooltip: "Remaining amount after payment adjustment",
      intentClass: "text-brown-500",
      iconBgClass: "bg-brown-100",
      valueClassName: "!text-brown-500",
      icon: Wallet,
    },
    {
      id: "receivable",
      title: "Receivable Amount",
      value: Number(
        Number(analyticsData?.aggregate_invoice_summary?.total as any)?.toFixed(
          2
        )
      ),
      intentClass: "text-red-600",
      iconBgClass: "bg-red-100",
      valueClassName: "!text-red-600",
      icon: CurrencyInr,
    },
    {
      id: "net_receivable",
      title: "Net Receivable Amount",
      value: Number(
        Number(
          (analyticsData?.aggregate_invoice_summary?.total || 0) -
            (analyticsData?.aggregate_invoice_summary
              ?.department_settlement_remaining_amount || 0)
        )?.toFixed(2)
      ),
      tooltip: "Net Receivable Amount = Receivable Amount - Balance Amount",
      intentClass: "text-red-600",
      iconBgClass: "bg-red-100",
      valueClassName: "!text-red-600",
      icon: CurrencyInr,
    },
  ]
  return (
    <>
      <header className="grid grid-cols-3 justify-between p-2 border-b-1 border-b-gray-300">
        <SegmentedControl
          value={"invoice"}
          onChange={value => {
            if (value === "plant") {
              navigate(AppRoutes.plantSettlement)
            }
          }}
          data={[
            {
              label: "Plant Settlement",
              value: "plant",
              icon: <List className="mr-1" />,
            },
            {
              label: "Invoice Settlement",
              value: "invoice",
              icon: <Receipt className="mr-1" />,
            },
          ].filter(b => b)}
        />
        <div className="flex items-center gap-3">
          <InputGroup
            className={"w-full"}
            leftElement={<MagnifyingGlass />}
            inputProps={{
              placeholder: "Search by Invoice No., Customer name ",
              value: search || "",
              onChange(e) {
                params.set("search", e.target.value)
                params.delete("page")
                setParams(params)
              },
            }}
          />
          <PayableFilter
            filter={filter}
            setFilter={setFilter}
            hideAdvanceStatus
            hideAdvanceType
            hideInactive
            hideTradeKind
            isTransactionStatusMulti
          />
        </div>

        <div className="flex gap-4 justify-self-end">
          <DownloadExcelButton
            text="Download Excel"
            getData={getExcelRows}
            fileName={`invoice-settlement-${dayjs().format(
              "YYYY-MM-DD-HHmm"
            )}.xlsx`}
            sheetName="Invoice Settlement"
            columnWidths={[
              12, 12, 14, 20, 24, 18, 14, 12, 12, 16, 18, 18, 18, 18, 30,
            ]}
            amountColumns={[
              "Invoice Value",
              "Credit Note",
              "Debit Note",
              "Received Amount",
              "Receivable Amount",
            ]}
            variant="ghost"
            leftIcon={<DownloadSimple />}
          />
          {canUploadPayableReceivable && <SettlementActions />}
        </div>
      </header>
      <InvoiceTransactionModal
        historyCols={`!grid-cols-7`}
        historyColSpan={`!col-span-5`}
        columns={historyColumns}
      />
      <section className="m-4 bg-white p-4 mb-0 rounded-t-xl">
        <div className="grid grid-cols-7 gap-4">
          <TableSummary summary={summaryCards} loading={analyticsLoading} />
        </div>
      </section>
      <RemarkModal
        useAddRemarkMutation={useUpdateInvoiceSettlementPaymentMutation}
        refetch={refetchList}
        title="Remark"
      />
      <TableView
        relative={false}
        className="!mt-0 overflow-x-auto"
        tableClassName="!mt-0 !min-h-[600px] max-h-[600px] overflow-y-scroll"
        paginationProps={{
          total: Math.ceil(totalRows / siteConfig.pageSize),
          page: page,
          onChange(page) {
            params.set("page", page.toString())
            params.set("search", "")
            setParams(params)
          },
        }}
        isLoading={listFetching}
        columns={COLUMNS}
        data={data?.invoice || []}
        meta={<>{totalRows} results</>}
        aggregateColumnClassName={
          "bg-blue-100 hover:bg-blue-100 p-10 sticky bottom-0 z-[8] font-600"
        }
      />
      <ReturnTransactionsModal
        invoiceId={invoiceId}
        isOpen={!!invoiceId}
        onClose={refetch => {
          setInvoiceId(undefined)
          if (!!refetch) {
            setTimeout(() => {
              refetchList({
                requestPolicy: "network-only",
              })
            }, 0)
          }
        }}
      />
    </>
  )
}

export default InvoiceSettlement
